import {
  CopyableText,
  DataGrid,
  formatDate,
  StatusBadge
} from '@ijoin/components';
import {
  GetAdminPlanEquityWashRuleRes,
  useGetAdminPlanEquityWashRules,
  useGetPlan
} from '@ijoin/hooks';
import { Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import { FC } from 'react';

const ruleStatus = (startDate: string, endDate: string) => {
  const now = new Date();

  if (new Date(startDate) > now) {
    return { label: 'Scheduled', severity: 'info' as const };
  }

  if (new Date(endDate) < now) {
    return { label: 'Expired', severity: 'warning' as const };
  }

  return { label: 'Active', severity: 'success' as const };
};

const columns: GridColDef<GetAdminPlanEquityWashRuleRes>[] = [
  {
    field: 'assetsFromFundId',
    headerName: 'From CUSIP',
    width: 130
  },
  {
    field: 'assetsFromFundDescription',
    flex: 1,
    headerName: 'From Fund'
  },
  {
    field: 'assetsToFundId',
    headerName: 'To CUSIP',
    width: 130
  },
  {
    field: 'assetsToFundDescription',
    flex: 1,
    headerName: 'To Fund'
  },
  {
    field: 'startDate',
    headerName: 'Start',
    valueFormatter: value => (value ? formatDate(value) : ''),
    width: 120
  },
  {
    field: 'endDate',
    headerName: 'End',
    valueFormatter: value => (value ? formatDate(value) : ''),
    width: 120
  },
  {
    field: 'status',
    headerName: 'Status',
    renderCell: params => {
      const status = ruleStatus(
        String(params.row.startDate),
        String(params.row.endDate)
      );

      return (
        <StatusBadge severity={status.severity}>{status.label}</StatusBadge>
      );
    },
    sortable: false,
    width: 110
  },
  {
    field: 'updatedAt',
    headerName: 'Updated',
    valueFormatter: value => (value ? formatDate(value) : ''),
    width: 120
  }
];

export const PlanEquityWashTab: FC<{ planId: string }> = props => {
  const plan = useGetPlan(props.planId);
  const rules = useGetAdminPlanEquityWashRules(props.planId);

  return (
    <Stack spacing={2}>
      <Stack alignItems='center' direction='row' spacing={0.5}>
        <Typography color='text.secondary' variant='body2'>
          Integration ID
        </Typography>
        <Typography sx={{ fontFamily: 'monospace' }} variant='body2'>
          {plan.data?.integrationId ?? 'None'}
        </Typography>
        {plan.data?.integrationId && (
          <CopyableText value={plan.data.integrationId} />
        )}
      </Stack>
      <Stack alignItems='center' direction='row' spacing={0.5}>
        <Typography color='text.secondary' variant='body2'>
          Equity Wash
        </Typography>
        <StatusBadge
          data-testid='planHasEquityWash'
          severity={plan.data?.hasEquityWash ? 'success' : 'warning'}>
          {plan.data?.hasEquityWash ? 'Enabled' : 'Disabled'}
        </StatusBadge>
      </Stack>
      <DataGrid
        columns={columns}
        data-testid='planEquityWashGrid'
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => row.equityWashCompetingFundsId}
        loading={rules.isFetching}
        pageSizeOptions={[25, 50, 100]}
        rows={rules.data ?? []}
      />
    </Stack>
  );
};

PlanEquityWashTab.displayName = 'PlanEquityWashTab';
